import React, { useState } from 'react';

import { ThemeTag } from '@/features/analyze/components/Tag';
import { NodeType, Node } from '@/features/connect-dots/types/cytoscape';
import { useTranslation } from 'react-i18next';
import {
  HashtagIcon,
  TagIcon,
  ChevronUpIcon,
  XMarkIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { rwColors } from '@/styles/colors';

import { FilterItemMode } from '@/features/connect-dots/types/SelectedFilterItemsContextType';
import { useSelectedFilterItems } from '@/features/connect-dots/hooks/useSelectedFilterItems';
import { useThemes } from '@/features/theme/api/getThemes';
import clsx from 'clsx';
import { HoverTooltip } from '@/components/Elements/HoverTooltip';
import { useHoverTooltip } from '@/components/Elements/useHoverTooltip';
import { Core } from 'cytoscape';
import { useNodeHighlighting } from '@/features/connect-dots/hooks/useNodeHighlighting';

interface NodeTooltipProps {
  content: Node;
  onClose?: () => void;
  cytoscapeCoreRefObject?: Core | null;
}

export const TopicTooltipForFilter: React.FC<NodeTooltipProps> = ({
  content,
  onClose,
  cytoscapeCoreRefObject,
}: NodeTooltipProps) => {
  const { data: contractingThemes = [], isLoading: isThemesLoading } =
    useThemes();
  const { t } = useTranslation();
  const { addNode } = useSelectedFilterItems();

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const { hover, showHoverTooltip, hideHoverTooltip } = useHoverTooltip(50);

  const { highlightNodeAndConnections, removeHighlighting } =
    useNodeHighlighting();

  const [dropdownUp] = useState(false);

  if (!content) return null;

  const isThemeNameMatch = (() => {
    // テーマノードの場合、そのテーマが契約済みかチェック
    if (content.nodeType === NodeType.THEME) {
      const themeTableName = content.value;
      return (
        contractingThemes.some((theme) => theme.tableName === themeTableName) ??
        false
      );
    }

    // 関連テーマの中に一つでも契約済みのものがあれば選択可能
    return content.themes.some((themeTableName) =>
      contractingThemes.some((theme) => theme.tableName === themeTableName)
    );
  })();

  // ドロップダウンのオプション配列
  const filterOptions_topicTag = [
    {
      label: t('フィルター(NOT)に追加'),
      optionValue: 'add_to_not',
      modeValue: FilterItemMode.NOT,
    },
    {
      label: t('フィルター(OR)に追加'),
      optionValue: 'add_to_or',
      modeValue: FilterItemMode.OR,
    },
  ];

  const filterOptions_theme = [
    {
      label: t('フィルター(OR)に追加'),
      optionValue: 'add_to_or',
      modeValue: FilterItemMode.OR,
    },
  ];

  // ボタンクリック、オプション選択時の処理
  const handleOptionSelect = (
    modeValue: (typeof FilterItemMode)[keyof typeof FilterItemMode]
  ) => {
    const recordNode = {
      label: content.label,
      nodeType: content.nodeType,
      mode: modeValue,
      name: content.value,
      hasSelected: false, // 事前に選択されているわけではない
    };

    addNode(recordNode); // ノードを追加

    setDropdownOpen(false); // ドロップダウンを閉じる
    onClose?.();
  };

  // ドロップダウンの開閉
  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const actionDisabled = !isThemeNameMatch;
  // 背景色（無効時 / 有効時）
  const disabledBg = '#9ca3af'; // 無効時の色（任意で調整）
  const enabledBg = rwColors.main;
  const hoverBg = rwColors.sub;

  const TOOLTIP_WIDTH = 420;

  return (
    <div
      className="pointer-events-auto z-50 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-lg"
      style={{
        maxWidth: `${TOOLTIP_WIDTH}px`,
      }}
    >
      {/* ヘッダー部分 */}
      <div
        className={clsx(
          'mb-1 flex items-center justify-between gap-2',
          content.nodeType === NodeType.THEME
            ? 'rounded-t-md bg-white/100 p-1'
            : 'rounded-t-md bg-white/100 p-1'
        )}
      >
        {/* themeタグの表示 */}
        {content.nodeType === NodeType.THEME ? (
          <span className="text-xs font-semibold text-rw-sub">
            <HashtagIcon className="mr-1 inline-block size-4" />
            {t('テーマ')}
          </span>
        ) : (
          <div>
            {/* ISSUE TAG */}
            {content.nodeType === NodeType.CROSS_ISSUE ||
            content.nodeType === NodeType.SOCIAL_ISSUE ? (
              <span className="text-xs font-semibold text-red-500">
                <TagIcon className="mr-1 inline-block size-4" />
                {t('Issue Tags')}
              </span>
            ) : (
              <span className="text-xs font-semibold text-gray-500">
                <TagIcon className="mr-1 inline-block size-4" />
                {t('Normal Tags')}
              </span>
            )}
          </div>
        )}

        {/* クローズボタン */}
        <button
          className={clsx(
            'justify-end rounded-full p-1',
            content.nodeType === NodeType.THEME
              ? 'bg-white/100 text-gray-400 hover:bg-gray-100'
              : 'bg-white/100 text-gray-400 hover:bg-gray-100'
          )}
          onClick={onClose}
          aria-label={t('閉じる')}
        >
          <XMarkIcon className="size-4" />
        </button>
      </div>

      {/* タイトル */}
      <div className="mb-1 text-lg font-bold leading-tight">
        {content.label}
      </div>

      {/* サブ説明（例: description） */}
      {content.nodeType !== NodeType.THEME &&
        (content.description ? (
          <div className="mb-2 text-xs text-gray-600">
            {content.description}
          </div>
        ) : (
          <div className="mb-2 text-xs text-gray-600">{t('説明未設定')}</div>
        ))}

      {/* 関連テーマ */}
      {content.nodeType !== NodeType.THEME && content.themes.length > 0 && (
        <>
          {t('関連テーマ')}:
          <div className="flex flex-wrap gap-1">
            {content.themes.map((themeTableName) => (
              <ThemeTag
                key={themeTableName}
                icon={HashtagIcon}
                tableName={themeTableName}
                onClick={() => {
                  // クリックでツールチップを閉じて、テーマノードをハイライト
                  onClose?.();
                  if (!cytoscapeCoreRefObject) return;
                  const targetNode = cytoscapeCoreRefObject
                    .nodes()
                    .filter((node) => {
                      return (
                        node.data('nodeType') === NodeType.THEME &&
                        node.data('value') === themeTableName
                      );
                    });
                  if (targetNode.length > 0) {
                    const target = targetNode[0];
                    // 既存の選択を解除
                    removeHighlighting(cytoscapeCoreRefObject);
                    // センタリング
                    cytoscapeCoreRefObject.animate({
                      center: { eles: target },
                      duration: 500,
                      easing: 'ease-in-out',
                      zoom: 0.8,
                    });
                    // クリックされたテーマノードを選択状態にする
                    highlightNodeAndConnections(
                      cytoscapeCoreRefObject,
                      target.id()
                    );
                  }
                }}
              />
            ))}
          </div>
        </>
      )}

      {/* フィルター選択中表示 */}
      {content.isSelected && (
        <div className="mt-2 text-xs font-bold text-blue-500">
          {t('フィルターで選択中')}
        </div>
      )}

      {/* スプリットボタン（フィルターに追加＋ドロップダウン） */}
      {!content.isSelected && (
        <div
          className="pointer-events-auto relative mt-3 flex rounded-b-lg"
          style={{ backgroundColor: rwColors.main }}
        >
          {/* 主アクションボタン */}
          <button
            className={clsx(
              'pointer-events-auto flex-1 rounded-l  px-4 py-1 text-sm font-semibold text-white',
              actionDisabled
                ? 'cursor-not-allowed opacity-50'
                : 'cursor-pointer',
              `backgroundColor: ${actionDisabled ? disabledBg : enabledBg}`,
              `opacity: ${actionDisabled ? 0.5 : 1}`
            )}
            onClick={() => handleOptionSelect(FilterItemMode.AND)}
            disabled={actionDisabled}
            onMouseEnter={(e) => {
              if (!actionDisabled) {
                e.currentTarget.style.backgroundColor = hoverBg;
              }
            }}
            onMouseLeave={(e) => {
              if (!actionDisabled) {
                e.currentTarget.style.backgroundColor = enabledBg;
              }
            }}
          >
            {isThemesLoading ? t('loading...') : t('フィルター(AND)に追加')}
          </button>
          {/* ドロップダウンボタン */}
          <div
            className="pointer-events-auto relative"
            style={{ cursor: 'pointer', backgroundColor: rwColors.main }}
          >
            <button
              className={clsx(
                'pointer-events-auto flex items-center rounded-r border-l border-white p-1',
                actionDisabled
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-pointer',
                `backgroundColor: ${actionDisabled ? disabledBg : enabledBg}`
              )}
              style={
                {
                  '--hover-bg-color': rwColors.sub,
                } as React.CSSProperties
              }
              disabled={actionDisabled}
              onMouseEnter={(e) => {
                if (!actionDisabled) {
                  e.currentTarget.style.backgroundColor = hoverBg;
                }
              }}
              onMouseLeave={(e) => {
                if (!actionDisabled) {
                  e.currentTarget.style.backgroundColor = enabledBg;
                }
              }}
              onClick={() => {
                if (!actionDisabled) toggleDropdown();
              }}
            >
              {isThemesLoading ? (
                ''
              ) : dropdownUp ? (
                <ChevronUpIcon className="h-6 w-4 font-bold text-white" />
              ) : (
                <ChevronDownIcon className="h-6 w-4 font-bold text-white" />
              )}
            </button>
            {/* ドロップダウンメニュー */}
            {dropdownOpen && (
              <div
                className={clsx(
                  `pointer-events-auto absolute z-10 w-48 overflow-hidden rounded-lg border border-white/20 shadow-xl`,
                  dropdownUp ? 'bottom-full mb-2' : 'top-full mt-2'
                )}
                style={{
                  backgroundColor: rwColors.main,
                  ...(dropdownUp ? { right: 0 } : { right: 0 }),
                }}
              >
                {content.nodeType == NodeType.THEME
                  ? filterOptions_theme.map((opt, index) => (
                      <button
                        key={opt.optionValue}
                        onClick={(e) => {
                          if (actionDisabled) {
                            showHoverTooltip(
                              e,
                              t(
                                'このテーマの記事を閲覧するには有料契約が必要です。'
                              )
                            );
                          } else {
                            handleOptionSelect(opt.modeValue);
                          }
                        }}
                        className={clsx(
                          'w-full px-4 py-3 text-left text-sm font-medium text-white transition-colors duration-150',
                          actionDisabled
                            ? 'cursor-not-allowed opacity-50'
                            : 'cursor-pointer hover:bg-white/10',
                          index < filterOptions_theme.length - 1 &&
                            'border-b border-white/10'
                        )}
                        style={
                          {
                            backgroundColor: actionDisabled
                              ? disabledBg
                              : enabledBg,
                            pointerEvents: 'auto',
                          } as React.CSSProperties
                        }
                        onMouseEnter={(e) => {
                          if (!actionDisabled) {
                            e.currentTarget.style.backgroundColor = hoverBg;
                          } else {
                            showHoverTooltip(
                              e,
                              t(
                                'このテーマの記事を閲覧するには有料契約が必要です。'
                              )
                            );
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!actionDisabled) {
                            e.currentTarget.style.backgroundColor = enabledBg;
                          }
                          hideHoverTooltip();
                        }}
                      >
                        {opt.label}
                      </button>
                    ))
                  : filterOptions_topicTag.map((opt, index) => (
                      <button
                        key={opt.optionValue}
                        onClick={(e) => {
                          if (actionDisabled) {
                            showHoverTooltip(
                              e,
                              t(
                                'このテーマの記事を閲覧するには有料契約が必要です。'
                              )
                            );
                          } else {
                            handleOptionSelect(opt.modeValue);
                          }
                        }}
                        className={clsx(
                          'w-full px-4 py-3 text-left text-sm font-medium text-white transition-colors duration-150',
                          actionDisabled
                            ? 'cursor-not-allowed opacity-50'
                            : 'cursor-pointer hover:bg-white/10',
                          index < filterOptions_topicTag.length - 1 &&
                            'border-b border-white/10'
                        )}
                        style={
                          {
                            backgroundColor: actionDisabled
                              ? disabledBg
                              : enabledBg,
                            pointerEvents: 'auto',
                          } as React.CSSProperties
                        }
                        onMouseEnter={(e) => {
                          if (!actionDisabled) {
                            e.currentTarget.style.backgroundColor = hoverBg;
                          } else {
                            showHoverTooltip(
                              e,
                              t(
                                'このテーマの記事を閲覧するには有料契約が必要です。'
                              )
                            );
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!actionDisabled) {
                            e.currentTarget.style.backgroundColor = enabledBg;
                          }
                          hideHoverTooltip();
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
              </div>
            )}
          </div>
        </div>
      )}
      <HoverTooltip {...hover} />
    </div>
  );
};
